import { type ReactNode, useEffect } from 'react';

import { LoadingScreen } from '@truvhq/ui';
import { usePageVisibility } from 'react-page-visibility';

import { useAuth } from 'shared/lib/auth/useAuth';
import { authDebug } from 'shared/lib/debug/authDebugger';
import { useSessionExpiredModal } from 'shared/ui/useSessionExpiredModal';
import { Error401UserView } from 'views/errors/Error401View';
import { ErrorNotAuthenticated } from 'views/errors/ErrorNotAuthenticated';

import { UnapprovedCompanyMember } from './UnapprovedUser/UnapprovedCompanyMember';
import { UnapprovedUser } from './UnapprovedUser/UnapprovedUser';
import { UnverifiedEmail } from './UnverifiedEmail/UnverifiedEmail';

type AuthGuardProps = {
  children?: ReactNode;
};

export const AuthGuard = ({ children }: AuthGuardProps) => {
  const { isAuthenticated, wasAuthenticated, checkAuth, user, isGotError401, error, authFailed, authFailedError } =
    useAuth();

  const isVisible = usePageVisibility();

  const { handleSessionExpired } = useSessionExpiredModal();

  useEffect(() => {
    if (isVisible && !isAuthenticated && wasAuthenticated) {
      handleSessionExpired();

      return;
    }

    checkAuth();
  }, [checkAuth, handleSessionExpired, isAuthenticated, isGotError401, isVisible, wasAuthenticated]);

  // this is initial login
  if (!wasAuthenticated && !authFailed && !isAuthenticated) {
    authDebug.log('AuthGuard', 'Triggering initial login', {
      wasAuthenticated,
      authFailed,
      isAuthenticated,
    });
    // void login();
    window.location.href = `${window.location.origin}/login`;

    return <LoadingScreen className="min-h-full" />;
  }

  if (authFailed) {
    return <ErrorNotAuthenticated error={authFailedError} />;
  }

  if (isGotError401) {
    return <Error401UserView error={error} />;
  }

  if (!user?.email_verified) {
    return <UnverifiedEmail />;
  }

  if (!user?.is_owner && !!user?.company && !user?.is_approved) {
    return <UnapprovedCompanyMember declined={user?.is_approved === false} />;
  }

  if (!user?.is_approved) {
    return <UnapprovedUser />;
  }

  return <>{children}</>;
};
